import { useState } from 'react'
import { PlusIcon, TrashIcon, DocumentArrowDownIcon } from '@heroicons/react/24/outline'

/**
 * Página de administración de facturas para la clínica
 * Esta página permite a las secretarias crear servicios y generar IDs para los pacientes
 */
const ClinicaFactura = () => {
  // Estados para el formulario de nuevo servicio
  const [formData, setFormData] = useState({
    patientName: '',
    serviceType: '',
    servicePrice: '',
    patientEmail: '',
    notes: ''
  })

  // Estado para la lista de servicios generados
  const [generatedServices, setGeneratedServices] = useState([
    // Datos de demostración
    {
      id: 'SERV-12345',
      patientName: 'María García López',
      serviceType: 'Consulta cardiológica general',
      servicePrice: 800,
      patientEmail: 'maria.garcia@email.com',
      createdAt: new Date().toISOString(),
      status: 'pending'
    },
    {
      id: 'SERV-67890',
      patientName: 'Juan Pérez Martinez',
      serviceType: 'Electrocardiograma de 12 derivaciones',
      servicePrice: 1200,
      patientEmail: 'juan.perez@email.com',
      createdAt: new Date(Date.now() - 86400000).toISOString(),
      status: 'completed'
    }
  ])

  // Estado para controlar loading
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState(null)
  const [success, setSuccess] = useState(null)

  // Opciones de servicios médicos disponibles
  const serviceTypes = [
    { value: 'consulta_general', label: 'Consulta cardiológica general', price: 800 },
    { value: 'electrocardiograma', label: 'Electrocardiograma de 12 derivaciones', price: 1200 },
    { value: 'ecocardiograma', label: 'Ecocardiograma transtorácico', price: 2500 },
    { value: 'holter', label: 'Monitoreo Holter de 24 horas', price: 1800 },
    { value: 'prueba_esfuerzo', label: 'Prueba de esfuerzo', price: 2000 },
    { value: 'cateterismo', label: 'Cateterismo cardíaco', price: 8500 },
    { value: 'ablacion', label: 'Ablación por radiofrecuencia', price: 15000 }
  ]

  // Manejar cambios en el formulario
  const handleInputChange = (e) => {
    const { name, value } = e.target
    
    if (name === 'serviceType') {
      // Auto-completar el precio cuando se selecciona un servicio
      const selectedService = serviceTypes.find(s => s.value === value)
      setFormData(prev => ({
        ...prev,
        [name]: value,
        servicePrice: selectedService ? selectedService.price : ''
      }))
    } else {
      setFormData(prev => ({
        ...prev,
        [name]: value
      }))
    }
  }

  // Generar ID único para el servicio
  const generateServiceId = () => {
    const prefix = 'SERV-'
    const randomNum = Math.floor(Math.random() * 90000) + 10000
    return prefix + randomNum
  }

  // Manejar envío del formulario
  const handleSubmit = async (e) => {
    e.preventDefault()
    setIsLoading(true)
    setError(null)
    setSuccess(null)

    try {
      // Generar nuevo servicio
      const newService = {
        id: generateServiceId(),
        patientName: formData.patientName,
        serviceType: serviceTypes.find(s => s.value === formData.serviceType)?.label || formData.serviceType,
        servicePrice: parseFloat(formData.servicePrice),
        patientEmail: formData.patientEmail,
        notes: formData.notes,
        createdAt: new Date().toISOString(),
        status: 'pending'
      }

      // Agregar a la lista
      setGeneratedServices(prev => [newService, ...prev])

      // Limpiar formulario
      setFormData({
        patientName: '',
        serviceType: '',
        servicePrice: '',
        patientEmail: '',
        notes: ''
      })

      setSuccess(`Servicio creado exitosamente. ID: ${newService.id}`)
    } catch (err) {
      setError('Error al crear el servicio')
    } finally {
      setIsLoading(false)
    }
  }

  // Eliminar servicio
  const handleDeleteService = (serviceId) => {
    setGeneratedServices(prev => prev.filter(service => service.id !== serviceId))
    setSuccess('Servicio eliminado correctamente')
  }

  // Simular descarga de PDF
  const handleDownloadPdf = (serviceId) => {
    // En una implementación real, esto haría una petición al backend
    alert(`Descargando PDF para el servicio ${serviceId}`)
  }

  // Formatear fecha
  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('es-MX', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    })
  }

  return (
    <div className="min-h-screen bg-gray-50 py-8 px-4 sm:px-6 lg:px-8">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="mb-8 text-center">
          <h1 className="text-3xl sm:text-4xl font-bold text-primary">Administración de Facturas</h1>
          <p className="mt-2 text-gray-600">Sistema para generar IDs de servicio para los pacientes</p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          {/* Formulario para crear nuevo servicio */}
          <div className="bg-white rounded-xl shadow-md p-6 sm:p-8">
            <h2 className="text-2xl font-bold text-primary mb-6 flex items-center">
              <PlusIcon className="h-6 w-6 mr-2" />
              Crear Nuevo Servicio
            </h2>

            <form onSubmit={handleSubmit} className="space-y-6">
              {/* Nombre del paciente */}
              <div>
                <label htmlFor="patientName" className="block text-sm font-medium text-gray-700 mb-1">
                  Nombre del paciente *
                </label>
                <input
                  id="patientName"
                  name="patientName"
                  type="text"
                  required
                  value={formData.patientName}
                  onChange={handleInputChange}
                  placeholder="Nombre completo del paciente"
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary/20 focus:border-primary transition-colors"
                />
              </div>

              {/* Tipo de servicio */}
              <div>
                <label htmlFor="serviceType" className="block text-sm font-medium text-gray-700 mb-1">
                  Tipo de servicio *
                </label>
                <select
                  id="serviceType"
                  name="serviceType"
                  required
                  value={formData.serviceType}
                  onChange={handleInputChange}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary/20 focus:border-primary transition-colors"
                >
                  <option value="">Selecciona un servicio</option>
                  {serviceTypes.map((service) => (
                    <option key={service.value} value={service.value}>
                      {service.label} - ${service.price}
                    </option>
                  ))}
                </select>
              </div>

              {/* Precio del servicio */}
              <div>
                <label htmlFor="servicePrice" className="block text-sm font-medium text-gray-700 mb-1">
                  Precio del servicio *
                </label>
                <input
                  id="servicePrice"
                  name="servicePrice"
                  type="number"
                  required
                  min="0"
                  step="0.01"
                  value={formData.servicePrice}
                  onChange={handleInputChange}
                  placeholder="0.00"
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary/20 focus:border-primary transition-colors"
                />
              </div>

              {/* Email del paciente */}
              <div>
                <label htmlFor="patientEmail" className="block text-sm font-medium text-gray-700 mb-1">
                  Email del paciente *
                </label>
                <input
                  id="patientEmail"
                  name="patientEmail"
                  type="email"
                  required
                  value={formData.patientEmail}
                  onChange={handleInputChange}
                  placeholder="email@ejemplo.com"
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary/20 focus:border-primary transition-colors"
                />
              </div>

              {/* Notas adicionales */}
              <div>
                <label htmlFor="notes" className="block text-sm font-medium text-gray-700 mb-1">
                  Notas adicionales
                </label>
                <textarea
                  id="notes"
                  name="notes"
                  rows={3}
                  value={formData.notes}
                  onChange={handleInputChange}
                  placeholder="Información adicional sobre el servicio..."
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary/20 focus:border-primary transition-colors resize-none"
                />
              </div>

              {/* Botón de envío */}
              <button
                type="submit"
                disabled={isLoading}
                className="w-full py-3 px-4 rounded-lg font-semibold text-white bg-primary hover:bg-primary/90 focus:outline-none focus:ring-2 focus:ring-primary/20 transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isLoading ? 'Creando servicio...' : 'Crear Servicio'}
              </button>
            </form>

            {/* Mensajes de estado */}
            {error && (
              <div className="mt-4 p-4 bg-red-50 text-red-700 rounded-lg">
                <p className="font-medium">Error:</p>
                <p>{error}</p>
              </div>
            )}

            {success && (
              <div className="mt-4 p-4 bg-green-50 text-green-700 rounded-lg">
                <p>{success}</p>
              </div>
            )}
          </div>

          {/* Lista de servicios generados */}
          <div className="bg-white rounded-xl shadow-md p-6 sm:p-8">
            <h2 className="text-2xl font-bold text-primary mb-6">
              Servicios Generados ({generatedServices.length})
            </h2>

            <div className="space-y-4 max-h-96 overflow-y-auto">
              {generatedServices.length === 0 ? (
                <div className="text-center py-8 text-gray-500">
                  <p>No hay servicios generados aún</p>
                  <p className="text-sm">Crea el primer servicio usando el formulario</p>
                </div>
              ) : (
                generatedServices.map((service) => (
                  <div
                    key={service.id}
                    className="border border-gray-200 rounded-lg p-4 hover:shadow-md transition-shadow"
                  >
                    <div className="flex justify-between items-start mb-2">
                      <div className="flex-1">
                        <p className="font-semibold text-lg text-primary">{service.id}</p>
                        <p className="text-gray-900 font-medium">{service.patientName}</p>
                        <p className="text-gray-600 text-sm">{service.serviceType}</p>
                        <p className="text-gray-500 text-xs">{formatDate(service.createdAt)}</p>
                      </div>
                      <div className="text-right">
                        <p className="font-bold text-lg">${service.servicePrice.toFixed(2)}</p>
                        <span
                          className={`inline-block px-2 py-1 rounded-full text-xs font-medium ${
                            service.status === 'completed'
                              ? 'bg-green-100 text-green-800'
                              : service.status === 'pending'
                              ? 'bg-yellow-100 text-yellow-800'
                              : 'bg-gray-100 text-gray-800'
                          }`}
                        >
                          {service.status === 'completed' ? 'Completado' : 
                           service.status === 'pending' ? 'Pendiente' : 'Cancelado'}
                        </span>
                      </div>
                    </div>

                    {service.notes && (
                      <p className="text-gray-600 text-sm mb-3 bg-gray-50 p-2 rounded">
                        <strong>Notas:</strong> {service.notes}
                      </p>
                    )}

                    <div className="flex flex-wrap gap-2">
                      <button
                        onClick={() => handleDownloadPdf(service.id)}
                        className="inline-flex items-center px-3 py-1 text-xs font-medium text-blue-700 bg-blue-100 rounded-full hover:bg-blue-200 transition-colors"
                      >
                        <DocumentArrowDownIcon className="h-4 w-4 mr-1" />
                        Descargar PDF
                      </button>

                      {service.status === 'pending' && (
                        <button
                          onClick={() => handleDeleteService(service.id)}
                          className="inline-flex items-center px-3 py-1 text-xs font-medium text-red-700 bg-red-100 rounded-full hover:bg-red-200 transition-colors"
                        >
                          <TrashIcon className="h-4 w-4 mr-1" />
                          Cancelar
                        </button>
                      )}
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>
        </div>

        {/* Información adicional */}
        <div className="mt-8 bg-blue-50 rounded-xl p-6">
          <h3 className="text-lg font-semibold text-blue-900 mb-2">Instrucciones:</h3>
          <ul className="text-blue-800 space-y-1 text-sm">
            <li>• Complete la información del paciente y seleccione el tipo de servicio médico</li>
            <li>• El sistema generará automáticamente un ID único para el servicio</li>
            <li>• Proporcione este ID al paciente para que pueda generar su factura</li>
            <li>• El paciente puede usar este ID en la página principal de facturación</li>
            <li>• Puede descargar o cancelar servicios desde la lista de servicios generados</li>
          </ul>
        </div>
      </div>
    </div>
  )
}

export default ClinicaFactura
